// Invoice outstanding-balance math — the single source of truth for "how much
// does the client still owe on this invoice", shared by apps/api (the public
// pay routes + the mark-paid cascade) and the offline QA gate.
//
// WHY IT LIVES HERE. Same discipline as order-money.ts / upfront-payment.ts:
// the amount the /i/:uuid pay button charges, the amount the SO "Pay remaining
// balance" button charges, and the threshold the Stripe webhook uses to flip an
// invoice to paid MUST be one number. Duplicating the formula per surface is how
// they drift. One implementation, every caller.
//
// THE FORMULA (integer cents, no floating point).
//   netTotal   = max(0, total − depositApplied)   // balance_due, deposits netted
//   outstanding = max(0, netTotal − paid)          // minus the ledgered payments
// where:
//   • total          = invoice.total (goods + tax; the card fee is NEVER in here)
//   • depositApplied = sum of the order's PAID deposit invoices (deposit netting;
//                      the generated balance_due column already applies this)
//   • paid           = sum of this invoice's SUCCEEDED invoice_payments rows
//
// The card fee is a payment surcharge that rides the Stripe CHARGE only — it is
// never part of `total`, so it never inflates `outstanding`. That invariant is
// owned by upfront-payment.ts (computeUpfrontDue) and preserved here by refusing
// to accept a fee input at all.

/** Inputs are integer cents. Non-integers are truncated defensively. */
export interface InvoiceBalanceInput {
  /** invoice.total in integer cents (goods + tax; no card fee). */
  totalCents: number;
  /** Sum of the order's PAID deposit invoices in integer cents (deposit netting). */
  depositAppliedCents: number;
  /** Sum of this invoice's SUCCEEDED payments in integer cents. */
  paidCents: number;
}

/** balance_due in integer cents: total − deposit netting, floored at 0. */
export function invoiceNetTotalCents(input: InvoiceBalanceInput): number {
  const total = Math.max(0, Math.trunc(input.totalCents));
  const deposit = Math.max(0, Math.trunc(input.depositAppliedCents));
  return Math.max(0, total - deposit);
}

/**
 * What the client still owes, in integer cents. Clamped to ≥ 0 so an
 * overpayment (e.g. a full online payment that recorded base + card fee) can
 * never surface as a negative "you owe -$X".
 */
export function computeOutstandingCents(input: InvoiceBalanceInput): number {
  const paid = Math.max(0, Math.trunc(input.paidCents));
  return Math.max(0, invoiceNetTotalCents(input) - paid);
}

/**
 * Is the invoice fully paid? True once the succeeded payments cover the
 * deposit-netted balance (NOT the raw total — an invoice with a paid deposit
 * netted into deposit_applied is settled when the client pays the remaining
 * balance_due, which is < total). This is the exact threshold the Stripe
 * webhook uses to fire `mark_paid`.
 *
 * A zero-balance invoice (netTotal === 0) is considered paid.
 */
export function isInvoiceFullyPaid(input: InvoiceBalanceInput): boolean {
  return computeOutstandingCents(input) <= 0;
}
